import Typography from '@mui/material/Typography';
import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ILocationGroupClient } from '~/services/api/clientAllocation/types';
import { ReactComponent as DeleteIcon } from '~/ui/assets/images/delete.svg';
import ButtonIcon from '~/ui/components/common/ButtonIcon';

import AllocationClientRow from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/components/AllocationClientRow/AllocationClientRow';
import MemoField from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/components/MemoField/MemoField';
import {
  IEditAllocationFormValues,
  LocationGroupWithAllocation,
} from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { useEditClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/context/EditClientAllocationContext';
import useClientAvailabilityMessages from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/hooks/validateAllocations/useClientAvailabilityMessages';

import variables from '~/ui/assets/styles/colors.module.scss';

interface IClientRowProps {
  client: ILocationGroupClient;
  memberId: number;
  teamMemberIdx: number;
  fieldIdx: number;
  field: LocationGroupWithAllocation;
  isDisabled: boolean;
  onRemoveClient: (groupId: number, clientId: number) => void;
  onMarkChanged: () => void;
}

const LocationGroupClientRow: FC<IClientRowProps> = ({
  client,
  memberId,
  teamMemberIdx,
  fieldIdx,
  field,
  isDisabled,
  onRemoveClient,
  onMarkChanged,
}) => {
  const { alertMessage, noteMessage } = useClientAvailabilityMessages({
    clientId: client.id,
    memberId,
    teamMemberIdx,
  });

  return (
    <AllocationClientRow
      client={{
        clientId: client.id,
        photo: client.photo,
        name: client.name,
        isResolved: isDisabled,
        isCrisis: false,
      }}
      alertMessage={alertMessage}
      noteMessage={noteMessage}
      actions={
        <ButtonIcon
          disabled={isDisabled}
          onClick={() => onRemoveClient(field.locationGroupId, client.id)}
        >
          <DeleteIcon width={20} color={variables.colorRed} />
        </ButtonIcon>
      }
      memoField={
        <MemoField
          disabled={isDisabled}
          memoFieldName={`teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.clientMemos.${client.id}`}
          onMemoChange={onMarkChanged}
        />
      }
    />
  );
};

interface IProps {
  field: LocationGroupWithAllocation & { id: string };
  fieldIdx: number;
  teamMemberIdx: number;
  memberId: number;
  showGroupName: boolean;
  onRemoveClient: (groupId: number, clientId: number) => void;
  onMarkChanged: (id: number) => void;
}

const AllocationLocationGroupRowItem: FC<IProps> = ({
  field,
  fieldIdx,
  teamMemberIdx,
  memberId,
  showGroupName,
  onRemoveClient,
  onMarkChanged,
}): ReactElement => {
  const { locationGroups } = useEditClientAllocationContext();
  const { watch } = useFormContext<IEditAllocationFormValues>();

  const removedClientIds: number[] = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations.${fieldIdx}.removedClientIds` as const,
  );

  const allGroupClients = useMemo(
    () => locationGroups.find(lg => lg.locationGroup.id === field.locationGroupId)?.clients ?? [],
    [locationGroups, field.locationGroupId],
  );

  const visibleClients = useMemo(
    () =>
      allGroupClients.filter(client => {
        if ((removedClientIds || []).includes(client.id)) return false;
        if (field.allocation && !(client.id in field.clientMemos)) return false;
        return true;
      }),
    [allGroupClients, removedClientIds, field],
  );

  const isDisabled = !!field.allocation?.resolved;

  const handleMemoChange = useCallback(() => {
    if (field.allocation?.id) {
      onMarkChanged(field.allocation.id);
    }
  }, [field.allocation, onMarkChanged]);

  return (
    <>
      {showGroupName ? (
        <Typography variant="subtitle2">{field.locationGroupName}</Typography>
      ) : null}

      {visibleClients.map(client => (
        <LocationGroupClientRow
          key={client.id}
          client={client}
          memberId={memberId}
          teamMemberIdx={teamMemberIdx}
          fieldIdx={fieldIdx}
          field={field}
          isDisabled={isDisabled}
          onRemoveClient={onRemoveClient}
          onMarkChanged={handleMemoChange}
        />
      ))}
    </>
  );
};

export default AllocationLocationGroupRowItem;
